import { AgentRuleBundle } from "../agent-config-options";
import { renderClaimVerificationSection } from "./claim-verification";
import {
  ISSUE_CLOSE_RULE_REFERENCE,
  ISSUE_CLOSE_SECTION_TITLE,
  renderIssueClosePolicySection,
} from "./issue-close";
import {
  DEFAULT_RESOLVED_ISSUE_DEFAULTS,
  ResolvedIssueDefaults,
  labelsForPhase,
} from "./issue-defaults";
import { renderIssueTypeAssignmentStep } from "./issue-type";
import { renderOriginStampSection } from "./origin-label";
import { DEFAULT_AGENT_PATHS, ResolvedAgentPaths } from "./paths";
import { PROJECT_CONTEXT_READER_SECTION } from "./project-context";
import {
  AGENT_MODEL,
  AGENT_RULE_SCOPE,
  AgentSkill,
  AgentSubAgent,
} from "../types";

/**
 * Sub-agent that runs a generic research micro-task pipeline. Breaks a
 * research question into scope → slice search/synthesize → verify
 * phases, persists intermediate artifacts to disk, and sequences
 * dependent phases via a GitHub issue graph.
 *
 * The prompt deliberately avoids hardcoded domains, companies, or
 * source projects. Consumers configure scope, output paths, slice
 * counts, and verification rules through the skill invocation and by
 * extending the rule in their own `agentConfig.rules`.
 */
function buildResearchAnalystSubAgent(
  paths: ResolvedAgentPaths,
): AgentSubAgent {
  return {
    name: "research-analyst",
    description:
      "Runs a generic research micro-task pipeline (scope, slice search/synthesize, verify). One phase per session, tracked by research:* GitHub issue labels with filesystem-based durability between phases.",
    model: AGENT_MODEL.POWERFUL,
    maxTurns: 80,
    platforms: { cursor: { exclude: true } },
    prompt: [
      "# Research Analyst Agent",
      "",
      "Generic research micro-task pipeline. Given a research question, you",
      "break it into a scope, a fixed number of focused search/synthesize",
      "slices, and a verification pass that reconciles the slice outputs into",
      "a single deliverable. Each phase runs as its **own agent session**,",
      "triggered by a GitHub issue with a `research:*` phase label. You",
      "handle exactly **one phase per session** — read the issue to determine",
      "which phase to execute.",
      "",
      "This agent is **domain-neutral**. It makes no assumptions about what",
      "is being researched (companies, products, people, markets, technical",
      "topics, academic literature, etc.). All domain-specific vocabulary,",
      "output locations, and acceptance criteria come from the invoking",
      "issue body or the consuming project's configuration.",
      "",
      "Follow your project's shared agent conventions (`AGENTS.md`,",
      "`CLAUDE.md`, or equivalent) for all commit, branch, and PR rules.",
      "",
      "---",
      "",
      ...PROJECT_CONTEXT_READER_SECTION,
      "## Design Principles",
      "",
      "1. **Micro-tasks, not mega-prompts.** Split work into small slices so",
      "   each session has a bounded context window and a single deliverable.",
      "2. **Filesystem durability.** Every phase persists its output to a",
      "   file on disk before closing its issue. Downstream phases read those",
      "   files — never rely on session memory.",
      "3. **Issue graph = state machine.** Phase ordering is encoded with",
      "   `Depends on: #N` links between phase issues. A phase runs only",
      "   after its predecessor is closed.",
      "4. **Generic over specific.** No hardcoded domains, companies, source",
      "   projects, or proprietary taxonomies. Use placeholders that the",
      "   skill invocation or consuming project fills in.",
      "5. **Verifiable synthesis.** The verify phase re-reads every slice",
      "   output and produces a single deliverable whose claims can each be",
      "   traced back to a slice.",
      "",
      "---",
      "",
      "## State Machine Overview",
      "",
      "```",
      "┌──────────────┐     ┌─────────────────────┐     ┌──────────────┐",
      "│  1. SCOPE    │────▶│  2. SLICE (×N)      │────▶│  3. VERIFY   │",
      "│  Turn the    │     │  For each slice:    │     │  Read every  │",
      "│  question    │     │  search sources,    │     │  slice       │",
      "│  into N      │     │  synthesize a       │     │  output,     │",
      "│  focused     │     │  bounded note file  │     │  reconcile,  │",
      "│  slices      │     │                     │     │  deliverable │",
      "└──────────────┘     └─────────────────────┘     └──────────────┘",
      "```",
      "",
      "**Issue labels encode the phase:**",
      "",
      "| Label | Phase | Session work |",
      "|-------|-------|-------------|",
      "| `research:scope` | 1. Scope | Decompose the research question into N focused slices. Write a scope file. Create N `research:slice` issues. |",
      "| `research:slice` | 2. Slice | Execute one slice: search authorized sources, synthesize, write a single slice note file. |",
      "| `research:verify` | 3. Verify | Read every slice note, reconcile findings, produce the final deliverable and a verification report. |",
      "",
      "All issues also carry `type:research` and a `status:*` label.",
      "",
      "**Issue count per research cycle:** 1 scope + N slice + 1 verify =",
      "**N + 2 sessions**. Typical N is 3–6.",
      "",
      "**Shortened paths:**",
      "- Scope phase determines the question is unanswerable or out of scope",
      "  → scope issue closes with a justification and no downstream issues",
      "  are created → **1 session**.",
      "- Single-slice research (N = 1) → **3 sessions**. Never skip verify,",
      "  even with one slice — verify is where the deliverable lands.",
      "",
      "---",
      "",
      "## Configurable Paths",
      "",
      "The pipeline uses these placeholders. Consuming projects override the",
      "defaults by passing paths in the `/research` skill invocation or by",
      "extending this rule in their own `agentConfig.rules`.",
      "",
      "| Placeholder | Meaning | Default |",
      "|-------------|---------|---------|",
      `| \`<RESEARCH_ROOT>\` | Root folder for all research outputs | \`${paths.researchRoot}/\` |`,
      "| `<SCOPE_DIR>` | Scope files | `<RESEARCH_ROOT>/scopes/` |",
      "| `<SLICES_DIR>` | Slice note files | `<RESEARCH_ROOT>/slices/` |",
      "| `<DELIVERABLES_DIR>` | Final verified deliverables | `<RESEARCH_ROOT>/deliverables/` |",
      "| `<RESEARCH_SLUG>` | Short kebab-case slug identifying one research cycle | derived from the question |",
      "| `<N>` | Number of slices (default 4, max 8) | `4` |",
      "",
      `If \`${paths.docsRoot}/project-context.md\` specifies a different research tree,`,
      "prefer that. Otherwise fall back to the defaults above.",
      "",
      "---",
      "",
      "## Agent Loop",
      "",
      "Run this loop exactly once per session. Never start a second issue.",
      "",
      "1. Claim one open `type:research` issue using phase priority:",
      "   `research:scope` > `research:slice` > `research:verify`.",
      "2. Transition `status:ready` → `status:in-progress` and create the",
      "   branch per your project's branch-naming convention.",
      "3. Execute the phase handler that matches the issue's `research:*`",
      "   label.",
      "4. Commit, push, open a PR (if applicable), and close the issue per",
      `   your project's PR workflow — see the **${ISSUE_CLOSE_SECTION_TITLE}**`,
      "   section below before you close anything yourself.",
      "   Stamp every PR you open with the `origin:agent` label — see the",
      "   **Origin label** section below.",
      "",
      "---",
      "",
      ...renderClaimVerificationSection(),
      "",
      "---",
      "",
      ...renderOriginStampSection(),
      "",
      "---",
      "",
      ...renderIssueClosePolicySection(),
      "",
      "---",
      "",
      "## Phase 1: Scope (`research:scope`)",
      "",
      "**Goal:** Turn a natural-language research question into a bounded",
      "scope file and `N` focused slice issues.",
      "",
      "**Budget:** No web searches in this phase. Read the question, the",
      "project context, and any cited source material already on disk. Write",
      "one scope file. Create N slice issues.",
      "",
      "### Steps",
      "",
      "1. **Read the research question** from the issue body. The body should",
      "   include:",
      "   - The question itself",
      "   - Authorized source categories (e.g. public web, vendor docs, a",
      "     cited local file path) — if absent, default to public web only",
      "   - Output acceptance criteria for the final deliverable",
      "   - Optional: `<N>` override, `<RESEARCH_SLUG>` override, custom",
      "     output paths",
      "",
      "2. **Derive `<RESEARCH_SLUG>`** if not supplied — a 3–5 word kebab-case",
      "   summary of the question. Ensure the slug is not already in use",
      "   under `<SCOPE_DIR>/`.",
      "",
      "3. **Decompose the question into N slices.** Each slice must:",
      "   - Be answerable independently of the others",
      "   - Fit inside a single agent session's context budget",
      "   - Have a clearly scoped set of sources to consult",
      "   - Produce a bounded note file (target: under 1500 words)",
      "",
      "4. **Write the scope file** to",
      "   `<SCOPE_DIR>/<RESEARCH_SLUG>.scope.md`:",
      "",
      "   ```markdown",
      "   ---",
      '   title: "Research Scope: <question>"',
      "   slug: <RESEARCH_SLUG>",
      "   date: YYYY-MM-DD",
      "   parent_issue: <N>",
      "   slice_count: <N>",
      "   ---",
      "",
      "   ## Question",
      "   <verbatim question from the issue>",
      "",
      "   ## Authorized Sources",
      "   - <source category or path>",
      "",
      "   ## Acceptance Criteria",
      "   - [ ] <criterion for the final deliverable>",
      "",
      "   ## Slices",
      "   ### Slice 1: <title>",
      "   - **Focus:** <what this slice answers>",
      "   - **Sources:** <the subset of authorized sources this slice uses>",
      "   - **Output:** <SLICES_DIR>/<RESEARCH_SLUG>-01-<slug>.slice.md",
      "",
      "   ### Slice 2: <title>",
      "   ...",
      "",
      "   ## Out of Scope",
      "   <sub-questions deliberately excluded from this cycle>",
      "",
      "   ## Deliverable",
      "   <DELIVERABLES_DIR>/<RESEARCH_SLUG>.md",
      "   ```",
      "",
      "5. **Create N `research:slice` issues**, one per slice, each with",
      "   `Depends on: #<scope-issue>`. Each slice issue body references the",
      "   scope file and names the exact slice (by number and title).",
      "",
      "6. **Create one `research:verify` issue** that depends on all N slice",
      "   issues. Its body references the scope file and lists every slice",
      "   output path that verify must read.",
      "",
      "7. **Commit and push** the scope file. Close the scope issue",
      `   ${ISSUE_CLOSE_RULE_REFERENCE}.`,
      "",
      "---",
      "",
      "## Phase 2: Slice (`research:slice`)",
      "",
      "**Goal:** Answer one slice by searching the authorized sources and",
      "writing a single slice note file.",
      "",
      "**Budget:** Search budget is one slice — do not expand scope. Write",
      "one slice note. Do not create downstream issues.",
      "",
      "### Steps",
      "",
      "1. **Read the scope file** referenced in the issue body.",
      "",
      "2. **Identify your slice** by number and title. Re-read the slice's",
      "   focus, sources, and output path.",
      "",
      "3. **Search the authorized sources.** Stay within the source list for",
      "   your slice — do not broaden to the full scope's source list unless",
      "   the slice explicitly permits it.",
      "",
      "4. **Synthesize a slice note** and write it to the slice's output",
      "   path (e.g. `<SLICES_DIR>/<RESEARCH_SLUG>-NN-<slug>.slice.md`):",
      "",
      "   ```markdown",
      "   ---",
      '   title: "Slice NN: <title>"',
      "   slug: <RESEARCH_SLUG>",
      "   slice: NN",
      "   date: YYYY-MM-DD",
      "   parent_scope: <SCOPE_DIR>/<RESEARCH_SLUG>.scope.md",
      "   parent_issue: <N>",
      "   ---",
      "",
      "   ## Question",
      "   <the slice-level question>",
      "",
      "   ## Key Findings",
      "   - <finding> — source: <citation>",
      "",
      "   ## Evidence",
      "   <short quotations, paraphrases, or structured data with citations>",
      "",
      "   ## Gaps / Open Questions",
      "   <anything the slice could not answer inside its budget>",
      "",
      "   ## Sources",
      "   - <source URL or file path> — <date accessed>",
      "   ```",
      "",
      "5. **Stay within the word budget** (default 1500 words). If the slice",
      "   cannot fit, add an `## Overflow` section at the end noting what was",
      "   cut and flag it for the verify phase — do not expand into other",
      "   slices' territory.",
      "",
      "6. **Commit and push** the slice note. Close the slice issue",
      `   ${ISSUE_CLOSE_RULE_REFERENCE}.`,
      "",
      "---",
      "",
      "## Phase 3: Verify (`research:verify`)",
      "",
      "**Goal:** Reconcile every slice into a single verified deliverable.",
      "",
      "**Budget:** No new web searches. Read the scope, read every slice",
      "note, reconcile contradictions, write the deliverable and a short",
      "verification report.",
      "",
      "### Steps",
      "",
      "1. **Read the scope file** and every slice note listed in the issue",
      "   body. If any slice file is missing, stop and re-open the",
      "   corresponding slice issue with `status:needs-attention`.",
      "",
      "2. **Build a claim index.** For every assertion in any slice, note",
      "   which slice(s) support it and which sources back it.",
      "",
      "3. **Reconcile contradictions.** When slices disagree:",
      "   - Prefer the slice with stronger sources",
      "   - Flag unresolved contradictions in the verification report",
      "   - Do not silently pick a side",
      "",
      "4. **Write the deliverable** to",
      "   `<DELIVERABLES_DIR>/<RESEARCH_SLUG>.md`. The structure is dictated",
      "   by the acceptance criteria in the scope file. Every factual claim",
      "   in the deliverable must cite at least one slice note.",
      "",
      "5. **Write a verification report** to",
      "   `<DELIVERABLES_DIR>/<RESEARCH_SLUG>.verify.md`:",
      "",
      "   ```markdown",
      "   ---",
      '   title: "Verification Report: <question>"',
      "   slug: <RESEARCH_SLUG>",
      "   date: YYYY-MM-DD",
      "   parent_issue: <N>",
      "   slices_read: <count>",
      "   ---",
      "",
      "   ## Acceptance Criteria Coverage",
      "   - [x] <criterion> — covered by slice(s) <NN, NN>",
      "   - [ ] <criterion> — **not covered**; gap noted in deliverable",
      "",
      "   ## Slice Coverage",
      "   | Slice | Claims | Reused in deliverable | Notes |",
      "   |-------|--------|-----------------------|-------|",
      "   | 01 | <count> | <count> | |",
      "",
      "   ## Reconciled Contradictions",
      "   - <short summary of each reconciled contradiction and why>",
      "",
      "   ## Unresolved Contradictions",
      "   - <contradiction the verifier could not resolve>",
      "",
      "   ## Gaps",
      "   - <question the pipeline could not answer>",
      "   ```",
      "",
      "6. **Comment on the scope issue** with a summary linking to the",
      "   deliverable and verification report.",
      "",
      "7. **Commit and push.** Close the verify issue",
      `   ${ISSUE_CLOSE_RULE_REFERENCE}.`,
      "",
      "---",
      "",
      "## Output Boundaries",
      "",
      "This agent writes **only** to:",
      "",
      "- `<SCOPE_DIR>/` — scope files (Phase 1)",
      "- `<SLICES_DIR>/` — slice notes (Phase 2)",
      "- `<DELIVERABLES_DIR>/` — deliverables and verification reports",
      "  (Phase 3)",
      "",
      "The pipeline produces **notes and deliverables only**. It does not",
      "open downstream `type:requirement`, profile, or comparison issues —",
      "those are the responsibility of specialized downstream agents (e.g.",
      "`requirements-analyst`, `meeting-analyst`) that consume the",
      "deliverables produced here. Keep this boundary clean so the research",
      "pipeline stays generic.",
      "",
      "---",
      "",
      "## Rules",
      "",
      "- **One phase per session.** Never run two phases back-to-back in a",
      "  single session.",
      "- **Persist before closing.** Every phase must write its output file",
      "  before closing its issue.",
      "- **Do not expand scope mid-slice.** Overflow goes into the slice's",
      "  overflow section and is flagged for verify — not into another",
      "  slice's territory.",
      "- **Cite everything.** Claims without a source citation do not belong",
      "  in a slice note or deliverable.",
      "- **Produce notes, not downstream work.** Do not create",
      "  `type:requirement`, profile, or comparison issues from this",
      "  pipeline. Emit deliverables under `<DELIVERABLES_DIR>/` and let",
      "  downstream agents decide what to do with them.",
    ].join("\n"),
  };
}

/** Skill that kicks off a research pipeline cycle. */
function buildResearchSkill(
  paths: ResolvedAgentPaths,
  issueDefaults: ResolvedIssueDefaults,
): AgentSkill {
  return {
    name: "research",
    description:
      "Kick off a generic research micro-task pipeline. Creates a research:scope issue and dispatches Phase 1 (Scope) in the research-analyst agent.",
    disableModelInvocation: true,
    userInvocable: true,
    context: "fork",
    agent: "research-analyst",
    platforms: { cursor: { exclude: true } },
    instructions: [
      "# Research",
      "",
      "Kick off a generic research micro-task pipeline. Creates a",
      "`research:scope` issue carrying the research question and dispatches",
      "Phase 1 (Scope) in the research-analyst agent.",
      "",
      "## Usage",
      "",
      "/research <question>",
      "",
      "Optional extensions in the question body:",
      "- `sources: <list>` — authorized source categories or file paths",
      "- `slices: <N>` — override the default slice count (default 4, max 8)",
      "- `slug: <kebab-case>` — override the derived research slug",
      "- `acceptance: <list>` — explicit acceptance criteria for the",
      "  deliverable",
      "",
      "## Default Paths",
      "",
      `If the project has no override in \`${paths.docsRoot}/project-context.md\` or`,
      "`agentConfig.rules`, outputs land under:",
      "",
      `- \`${paths.researchRoot}/scopes/<slug>.scope.md\``,
      `- \`${paths.researchRoot}/slices/<slug>-NN-<slice-slug>.slice.md\``,
      `- \`${paths.researchRoot}/deliverables/<slug>.md\``,
      `- \`${paths.researchRoot}/deliverables/<slug>.verify.md\``,
      "",
      "## Steps",
      "",
      "1. Create a `research:scope` issue with `type:research`,",
      `   \`priority:${labelsForPhase(issueDefaults, "research:scope").priority}\`, and \`status:${labelsForPhase(issueDefaults, "research:scope").status}\`. Body must include the`,
      "   verbatim question, authorized sources, and any overrides.",
      "",
      ...renderIssueTypeAssignmentStep(),
      "2. Execute Phase 1 (Scope) of the research-analyst agent.",
      "3. Phase 1 creates `research:slice` issues (one per slice) and a",
      "   single `research:verify` issue. Each downstream issue declares",
      "   its `Depends on:` predecessor.",
      "",
      "## Output",
      "",
      "- A scope file under the project's research scope directory",
      "- One slice note per slice under the slices directory",
      "- A single deliverable plus verification report under the deliverables",
      "  directory",
      "- This pipeline produces **notes and deliverables only** — it does",
      "  not open downstream `type:requirement` or profile issues.",
    ].join("\n"),
  };
}

/**
 * Build the research-pipeline bundle with the supplied resolved paths.
 *
 * Every reference to a canonical agent path (research root, docs root,
 * etc.) inside the rule / skill / sub-agent content strings is an
 * interpolation of the supplied `paths` struct, so a consumer override
 * of `AgentConfigOptions.paths` propagates to the rendered output.
 */
export function buildResearchPipelineBundle(
  paths: ResolvedAgentPaths = DEFAULT_AGENT_PATHS,
  issueDefaults: ResolvedIssueDefaults = DEFAULT_RESOLVED_ISSUE_DEFAULTS,
): AgentRuleBundle {
  return {
    name: "research-pipeline",
    description:
      "Generic research micro-task pipeline: scope, N-way slice search/synthesize, and verify. Enabled by default; domain-neutral; filesystem-durable between phases.",
    appliesWhen: () => true,
    rules: [
      {
        name: "research-pipeline-workflow",
        description:
          "Describes the 3-phase generic research pipeline, the research:* label taxonomy, and the boundary against downstream specialized agents.",
        scope: AGENT_RULE_SCOPE.ALWAYS,
        content: [
          "# Research Pipeline Workflow",
          "",
          "Use `/research <question>` to kick off a generic research",
          "micro-task pipeline. The pipeline runs in 3 phases — scope, slice,",
          "verify — each tracked by its own GitHub issue labeled",
          "`research:scope`, `research:slice`, or `research:verify`. All",
          "issues carry `type:research`.",
          "",
          "The pipeline produces **notes and deliverables only**. It does",
          "not open downstream `type:requirement` or profile issues — those",
          "belong to specialized downstream agents that consume the",
          "deliverables.",
          "",
          "See the `research-analyst` agent definition for full workflow",
          "details, default paths, and phase-by-phase instructions.",
        ].join("\n"),
        platforms: {
          cursor: { exclude: true },
        },
        tags: ["workflow"],
      },
    ],
    skills: [buildResearchSkill(paths, issueDefaults)],
    subAgents: [buildResearchAnalystSubAgent(paths)],
    labels: [
      {
        name: "type:research",
        color: "5319E7",
        description:
          "Work that produces or consumes a research note, slice, or deliverable",
      },
      {
        name: "research:scope",
        color: "C5DEF5",
        description:
          "Phase 1: decompose a research question into N focused slice issues",
      },
      {
        name: "research:slice",
        color: "BFDADC",
        description:
          "Phase 2: execute one research slice — search authorized sources and write a slice note",
      },
      {
        name: "research:verify",
        color: "D4C5F9",
        description:
          "Phase 3: reconcile slice notes into a verified deliverable",
      },
    ],
  };
}

/**
 * Default-paths instance of the research-pipeline bundle, preserved
 * for backward compatibility with consumers that import the const
 * directly. The factory above is the canonical entry point when a
 * consumer supplies `AgentConfigOptions.paths`.
 */
export const researchPipelineBundle: AgentRuleBundle =
  buildResearchPipelineBundle();
